import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import RemoveCircleOutlinedIcon from "@mui/icons-material/RemoveCircleOutlined";
import { useSnapshotSection } from "@/components/snapshot/snapshotSection.context";
import { toSydneyDateTimeText } from "./snapshotDropzone.utils";

type PillTone = "success" | "warning" | "error" | "info" | "neutral";

/** The section's status as a pill beside its name; what it built or why it failed is the tooltip. */
export const SnapshotDropzoneStatus = () => {
  const section = useSnapshotSection();
  const built = section.builtSnapshot;
  const buildResult = section.build.result;

  let tone: PillTone = "neutral";
  let icon: ReactNode = <InboxOutlinedIcon sx={{ fontSize: 14 }} />;
  let label = "No files";
  let detail = "";
  if (section.status === "building") {
    tone = "info";
    icon = (
      <CircularProgress
        color="inherit"
        size={12}
        thickness={6}
      />
    );
    label = "Building snapshot…";
  }
  else if (section.status === "failed") {
    tone = "error";
    icon = <ErrorOutlinedIcon sx={{ fontSize: 14 }} />;
    label = "Build failed";
    detail = section.build.error ?? "";
  }
  else if (section.status === "stale") {
    tone = "warning";
    icon = <ErrorOutlinedIcon sx={{ fontSize: 14 }} />;
    label = "Out of date";
  }
  else if (section.status === "ok" && built) {
    tone = "success";
    icon = <CheckCircleOutlinedIcon sx={{ fontSize: 14 }} />;
    label = `Up to date · built ${toSydneyDateTimeText(built.builtAt)}`;
    detail = built.key;
    if (buildResult) {
      detail = `${built.key} — ${buildResult.totalRows} rows from ${buildResult.processed.length} file(s)`;
      if (buildResult.imported) detail = `${detail}; ${buildResult.imported.rows} stock levels imported`;
    }
  }
  else if (section.status === "none") {
    tone = "error";
    icon = <RemoveCircleOutlinedIcon sx={{ fontSize: 14 }} />;
    label = "No snapshot";
  }

  let bgcolor = "action.hover";
  let color = "text.secondary";
  if (tone !== "neutral") {
    bgcolor = `surface.${tone}`;
    color = `${tone}.main`;
  }

  return (
    <Tooltip title={detail}>
      <Box
        component="span"
        sx={{
          alignItems: "center",
          bgcolor,
          borderRadius: 999,
          color,
          display: "inline-flex",
          fontSize: 13,
          fontWeight: 600,
          gap: 0.75,
          pl: 0.75,
          pr: 1,
          py: 0.25,
          whiteSpace: "nowrap",
        }}
      >
        {icon}
        {label}
      </Box>
    </Tooltip>
  );
};
